import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { BdExportAsFileInput } from '../../export-input';
import { BdExportAsFileConfig } from '../../export.config';
import { BdUiLabelsInputComponent } from '@bindoc-projects/labels-lib/ui';
import { InputText } from 'primeng/inputtext';
import { IftaLabel } from 'primeng/iftalabel';
import { Checkbox } from 'primeng/checkbox';
import { BdUiSingleSelectComponent, BdUiSingleSelectConfig } from '@bindoc-projects/ui-lib/selection/ui';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

@Component({
  selector: 'bd-export-as-file-dialog-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="export-form flex flex-col gap-4">
      <div>
        <bd-ui-single-select
          [config]="exportTypeConfig"
          [selectOptions]="exportTypeOptions$"
          [initialSelection]="exportTypeInitial()"
          (selection)="onExportTypeChange($event)"
        ></bd-ui-single-select>
      </div>
      <div>
        <p-iftalabel>
          <input
            id="export-file-name"
            data-test="export-file-name"
            pInputText
            fluid
            variant="filled"
            [formControl]="fileNameFormControl"
          />
          <label for="export-file-name"
            >Name (.{{ input().exportType }})</label
          >
        </p-iftalabel>
      </div>
      @if (authenticated()) {
        <div class="flex items-center gap-2">
          <p-checkbox
            inputId="save-in-download-center"
            [binary]="true"
            [formControl]="saveInDownloadCenterFormControl"
          ></p-checkbox>
          <label for="save-in-download-center"
            >Zusätzlich im File-Center ablegen</label
          >
        </div>
        @if (input().saveInDownloadCenter) {
          <bd-ui-labels-input
            [labels]="input().labels || []"
            (labelsChange)="onLabelsChange($event)"
          >
          </bd-ui-labels-input>
        }
      }
    </div>
  `,
  imports: [
    ReactiveFormsModule,
    InputText,
    IftaLabel,
    Checkbox,
    BdUiLabelsInputComponent,
    BdUiSingleSelectComponent,
  ],
})
export class BdExportAsFileDialogFormComponent {
  public readonly input = input.required<BdExportAsFileInput>();
  public readonly config = input<BdExportAsFileConfig>();
  public readonly authenticated = input<boolean>(false);

  public readonly inputChange = output<BdExportAsFileInput>();

  public readonly exportTypeConfig: BdUiSingleSelectConfig<{
    value: 'csv' | 'xlsx';
    label: string;
  }> = {
    id: 'export-type-select',
    selectLabel: 'Typ',
    resetOption: false,
    option: {
      sortFnc: (a, b) => a.label.localeCompare(b.label),
      uniqueFnc: (option) => option.value,
      searchOptionAccessor: (option) => option.label,
    },
  };

  public readonly fileNameFormControl = new FormControl<string>(
    null,
    Validators.required,
  );
  public readonly saveInDownloadCenterFormControl = new FormControl<boolean>(false);

  public readonly exportTypeInitial = signal<{
    value: 'csv' | 'xlsx';
    label: string;
  } | null>(null);

  private readonly _exportTypeOptionsSignal = computed(() =>
    this.config()?.form?.availableExportTypes?.map((it) => ({
      value: it as 'csv' | 'xlsx',
      label: it === 'csv' ? 'CSV' : 'XLSX (Excel)',
    })) ?? [],
  );
  public readonly exportTypeOptions$ = toObservable(this._exportTypeOptionsSignal);

  constructor() {
    effect(() => {
      const inp = this.input();
      const filename = inp?.filename ?? null;
      // Only write back when the value actually differs. Every emit below is
      // echoed by the parent through `[input]`, and re-writing the identical
      // string into a focused input would needlessly reset its caret.
      if (filename !== this.fileNameFormControl.value) {
        this.fileNameFormControl.setValue(filename, { emitEvent: false });
      }
      this.saveInDownloadCenterFormControl.setValue(inp?.saveInDownloadCenter ?? false, { emitEvent: false });
      if (inp?.exportType) {
        this.exportTypeInitial.set({ value: inp.exportType, label: '' });
      }
    });

    effect(() => {
      const config = this.config();
      if (config?.form?.defaultLabels) {
        this.inputChange.emit({ ...untracked(() => this._currentInput()), labels: config.form.defaultLabels });
      }
    });

    effect(() => {
      if (!this.authenticated()) {
        this.inputChange.emit({ ...untracked(() => this._currentInput()), saveInDownloadCenter: false, labels: [] });
      }
    });

    // Propagate the typed name immediately. A debounce here used to leave the
    // parent holding the previous (default) name for 500ms, so any other change
    // in that window - or an export started right away - shipped the stale name.
    this.fileNameFormControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.inputChange.emit(this._currentInput());
      });

    this.saveInDownloadCenterFormControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((saveInDownloadCenter) => {
        const updated: BdExportAsFileInput = { ...this._currentInput(), saveInDownloadCenter };
        if (!saveInDownloadCenter) {
          updated.labels = [];
        }
        this.inputChange.emit(updated);
      });
  }

  public onExportTypeChange(change: { value: 'csv' | 'xlsx'; label: string }): void {
    this.inputChange.emit({ ...this._currentInput(), exportType: change.value });
  }

  public onLabelsChange(labels: string[]): void {
    this.inputChange.emit({ ...this._currentInput(), labels });
  }

  /**
   * The current input merged with what the form controls actually show, so an
   * emit never carries a form value the user has already replaced.
   */
  private _currentInput(): BdExportAsFileInput {
    const inp = this.input();
    return {
      ...inp,
      filename: this.fileNameFormControl.value ?? inp.filename,
      saveInDownloadCenter:
        this.saveInDownloadCenterFormControl.value ?? inp.saveInDownloadCenter,
    };
  }
}
